/**
 * Email Center → Email designs: the library of e-mail designs (owner,
 * 2026-09-30). The five built-ins (Start your free trial, Search is open,
 * Newsletter, Personal note, Trial ending) are read-only starting points;
 * "Save as new" keeps an edited copy. Saved designs can be changed, deleted,
 * or taken straight into a new campaign.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Btn, Loading, Msg, panelPost, useAdminGet } from '../../kit';
import { inputCls } from '../emailKit';
import { DesignBuilder } from './DesignBuilder';
import { DesignDoc, DesignRow, DesignsMeta, forWire, sendDesignToCampaign, withKeys } from './types';

const DesignLibrary: React.FC = () => {
  const q = useAdminGet<DesignsMeta>('email', 'designs', {}, []);
  const meta = q.data;
  const all: DesignRow[] = useMemo(() => [...(meta?.builtins || []), ...(meta?.saved || [])], [meta]);
  const [id, setId] = useState('');
  const [doc, setDoc] = useState<DesignDoc | null>(null);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [dirty, setDirty] = useState(false);
  const [asNew, setAsNew] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const row = all.find(r => r.id === id) || null;

  const open = (r: DesignRow) => {
    if (dirty && !window.confirm('Discard the unsaved changes to this design?')) return;
    setId(r.id); setDoc(withKeys(r.doc)); setName(r.name); setDesc(r.description || '');
    setDirty(false); setAsNew(null); setNote(null);
  };
  useEffect(() => {
    if (!id && all.length) open(all[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all.length]);

  const save = async (mode: 'update' | 'new') => {
    if (!doc) return;
    const nm = mode === 'new' ? (asNew || '').trim() : name.trim();
    if (!nm) { setNote({ kind: 'error', text: 'Give the design a name.' }); return; }
    setBusy(true); setNote(null);
    const { data, error } = await panelPost('email', 'design_save', {
      id: mode === 'update' ? id : '', name: nm, description: desc, doc: forWire(doc),
      based_on: row?.builtin ? row.id : (row?.based_on || ''),
    });
    setBusy(false);
    if (error) { setNote({ kind: 'error', text: error }); return; }
    setNote({ kind: 'ok', text: mode === 'new' ? `Saved as “${nm}”.` : 'Saved.' });
    setDirty(false); setAsNew(null);
    setId(data.id); setName(data.name); setDoc(withKeys(data.doc));
    q.reload();
  };
  const del = async () => {
    if (!row || row.builtin || !window.confirm(`Delete “${row.name}”? Campaigns already sent keep their copy.`)) return;
    setBusy(true);
    const { error } = await panelPost('email', 'design_delete', { id: row.id });
    setBusy(false);
    if (error) { setNote({ kind: 'error', text: error }); return; }
    setId(''); setDoc(null); setDirty(false);
    q.reload();
  };

  if (q.loading && !meta) return <Loading />;
  if (q.error) return <Msg kind="error">{q.error}</Msg>;
  if (!meta) return null;
  const item = (r: DesignRow) => (
    <button key={r.id} type="button" onClick={() => open(r)}
      className={`w-full text-left px-3 py-2 rounded-lg border ${r.id === id ? 'border-primary-500 bg-primary-100/60' : 'border-grayscale-300 bg-white hover:bg-grayscale-200/50'}`}>
      <div className="text-sm font-bold text-grayscale-1300">{r.name}</div>
      {r.description && <div className="text-[11px] text-grayscale-1100 line-clamp-2">{r.description}</div>}
    </button>
  );

  return (
    <div className="grid gap-4 lg:grid-cols-[16rem_minmax(0,1fr)]">
      <aside className="flex flex-col gap-2">
        <div className="text-[11px] font-bold uppercase tracking-wide text-grayscale-1100">Built-in (start from these)</div>
        {meta.builtins.map(item)}
        <div className="text-[11px] font-bold uppercase tracking-wide text-grayscale-1100 mt-3">Saved ({meta.saved.length})</div>
        {meta.saved.length ? meta.saved.map(item) : (
          <div className="text-xs text-grayscale-1100">None yet — open a built-in, change it, and “Save as new”.</div>
        )}
      </aside>
      <section className="flex flex-col gap-3 min-w-0">
        {note && <Msg kind={note.kind}>{note.text}</Msg>}
        {doc && row && (
          <>
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-grayscale-1200">Name</span>
                <input value={name} disabled={row.builtin} maxLength={120}
                  onChange={e => { setName(e.target.value); setDirty(true); }} className={`${inputCls} !w-64`} />
              </label>
              <label className="flex flex-col gap-1 flex-1 min-w-[14rem]">
                <span className="text-xs font-medium text-grayscale-1200">What it is for</span>
                <input value={desc} maxLength={500} onChange={e => { setDesc(e.target.value); setDirty(true); }} className={inputCls} />
              </label>
              <div className="flex flex-wrap gap-2 pb-0.5">
                {!row.builtin && <Btn small kind="primary" onClick={() => save('update')} disabled={busy || !dirty}>{busy ? 'Saving…' : 'Save'}</Btn>}
                {asNew === null ? (
                  <Btn small kind={row.builtin ? 'primary' : 'subtle'} onClick={() => setAsNew(row.builtin ? `${row.name} (copy)` : `${name} (copy)`)} disabled={busy}>Save as new…</Btn>
                ) : (
                  <span className="flex items-center gap-1">
                    <input autoFocus value={asNew} maxLength={120} onChange={e => setAsNew(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') save('new'); if (e.key === 'Escape') setAsNew(null); }}
                      className={`${inputCls} !w-52`} />
                    <Btn small kind="primary" onClick={() => save('new')} disabled={busy}>Save</Btn>
                    <Btn small onClick={() => setAsNew(null)}>Cancel</Btn>
                  </span>
                )}
                <Btn small onClick={() => sendDesignToCampaign(doc, name)} title="Start a new campaign with this design">Use in a campaign</Btn>
                {!row.builtin && <Btn small kind="danger" onClick={del} disabled={busy}>Delete</Btn>}
              </div>
            </div>
            {row.builtin && (
              <div className="text-xs text-grayscale-1100">
                A built-in design: change anything you like, then <b>Save as new</b> to keep it in the library.
              </div>
            )}
            {dirty && <div className="text-[11px] text-amber-800">Unsaved changes.</div>}
            <DesignBuilder doc={doc} onChange={d => { setDoc(d); setDirty(true); }}
              placeholders={meta.placeholders} fonts={meta.fonts} palette={meta.palette}
              maxBlocks={meta.limits.blocks} maxButtons={meta.limits.buttons} />
          </>
        )}
      </section>
    </div>
  );
};

export default DesignLibrary;
